import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ClipboardList, Clock, BookMarked, PlayCircle, Check } from "lucide-react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";
import { langDisplayName } from "@/lib/languages";
import { StudentExpiredNotice } from "@/pages/MyClasses";
import { ClassAssetList } from "@/pages/AssetAssignment";

const TAG = { completed: "bg-mint", in_progress: "bg-butter", not_started: "bg-white" };

// Student class home: school info + this learner's own assignments. No controls, no classmates' data.
export default function StudentClass() {
  const { classId } = useParams();
  const { t, locale } = useI18n();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/my-classes/${classId}`).then((r) => setC(r.data)).catch(() => setErr(true)); }, [classId]);

  if (err) return <AppLayout><div className="max-w-3xl mx-auto px-4 py-8"><p className="text-coral" data-testid="student-class-no-access">{t("edu.cls.noAccess")}</p><Link to="/my-classes" className="underline text-sm">{t("nav.myclasses")}</Link></div></AppLayout>;
  if (!c) return <AppLayout><div className="max-w-3xl mx-auto px-4 py-8"><div className="nb-card h-40 animate-pulse" /></div></AppLayout>;
  const d = (x) => new Date(x).toLocaleDateString(locale);

  return (
    <AppLayout>
      <Seo title={`${c.name} · FluentLoop`} noindex />
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="student-class-home">
        <Link to="/my-classes" className="text-sm text-gray-600 flex items-center gap-1" data-testid="student-class-back"><ChevronLeft className="w-4 h-4 rtl:rotate-180" /> {t("nav.myclasses")}</Link>
        <h1 className="font-cabinet font-extrabold text-3xl mt-2" data-testid="student-class-title"><bdi>{c.name}</bdi></h1>
        <p className="text-sm text-gray-600" data-testid="student-class-meta">
          <bdi>{c.organization?.name}</bdi> · {langDisplayName(c.target_language, locale)} · {c.level}
          {c.teacher_name && <> · {t("edu.stu.teacher")}: <bdi>{c.teacher_name}</bdi></>}
        </p>
        {c.description && <p className="text-sm mt-2" dir="auto">{c.description}</p>}
        {c.pilot_expired && <StudentExpiredNotice t={t} />}

        <ClassAssetList classId={classId} />
        <h2 className="font-cabinet font-extrabold text-2xl mt-8 flex items-center gap-2"><ClipboardList className="w-6 h-6" /> {t("edu.stu.assignments")}</h2>
        <div className="space-y-2 mt-3" data-testid="student-class-assignments">
          {c.assignments.length === 0 && <p className="text-sm text-gray-500" data-testid="student-class-no-assignments">{t("edu.stu.noAssignments")}</p>}
          {c.assignments.map((a) => (
            <Link key={a.assignment_id} to={`/assignment/${a.assignment_id}`} className="nb-card p-3 flex items-center justify-between gap-3" data-testid={`student-assignment-${a.assignment_id}`}>
              <div className="min-w-0">
                <div className="font-bold truncate" dir="auto">{a.title}</div>
                <div className="text-xs text-gray-500 flex flex-wrap gap-2 mt-0.5">
                  {a.level && <span>{a.level}</span>}
                  {a.due_date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t("edu.stu.due", { date: d(a.due_date) })}</span>}
                  {a.due_state && a.due_state !== "scheduled" && a.status !== "completed" && <span className="nb-tag text-[10px] py-0 bg-peach">{t(`edu.cls.due.${a.due_state}`)}</span>}
                </div>
              </div>
              <span className={`nb-tag text-xs shrink-0 ${TAG[a.status]}`} data-testid={`student-assignment-status-${a.assignment_id}`}>
                {a.status === "completed" ? <Check className="w-3 h-3 inline" /> : a.status === "in_progress" ? <PlayCircle className="w-3 h-3 inline" /> : null} {t(`edu.stu.status.${a.status}`)}
              </span>
            </Link>
          ))}
        </div>

        {c.assigned_vocabulary.length > 0 && (
          <div className="nb-card bg-lavender p-4 mt-8" data-testid="student-class-vocab">
            <h3 className="font-bold flex items-center gap-2"><BookMarked className="w-4 h-4" /> {t("edu.stu.classVocab")}</h3>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {c.assigned_vocabulary.map((v) => (
                <span key={v.term} className="nb-tag bg-white text-xs normal-case tracking-normal"><span dir="auto">{v.term}</span>{v.translation ? <span className="text-gray-500"> · <bdi>{v.translation}</bdi></span> : null}</span>
              ))}
            </div>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-6">{t("edu.stu.privacy")}</p>
      </div>
    </AppLayout>
  );
}
